import { ACTION_TYPE, REPORT_NAME } from "../constant/reportType";
import { Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { alertMessage } from "../../utils/AlertService";
import { useEffect, useRef, useState } from "react";
import * as Yup from 'yup';
import { Modal } from "react-bootstrap";
import Loading from "../Loading";
import { getLocalStorageData } from "../../utils/LocalStorageService";
import { format, parseISO } from "date-fns";

// Event Master & Event Type
import { useSaveEventMasterMutation, useUpdateEventMasterMutation, useGetEventMasterByIdMutation, useBulkSaveEventMasterMutation } from "../../redux-toolkit/services/eventMasterService";
import { useSaveEventTypeMutation, useUpdateEventTypeMutation, useGetEventTypeByIdMutation, useBulkSaveEventTypeMutation, useFindAllEventTypeMutation } from "../../redux-toolkit/services/eventTypeService";
import { allEventTypeDetails, setAllEventType } from "../../redux-toolkit/slices/eventTypeSlice";
import { useAppSelector } from "../../redux-toolkit/hooks";

// Sovereign Event Ledger
import {
    useSaveLedgerMutation,
    useUpdateLedgerMutation,
    useGetLedgerByIdMutation,
    useGenerateDRPlanMutation,
} from "../../redux-toolkit/services/sovereignEventLedgerService";
import { useFindAllEventMasterMutation } from "../../redux-toolkit/services/eventMasterService";
import { useFindAllEntityMasterMutation } from "../../redux-toolkit/services/entityMasterService";
import { useFindAllAssetsMutation } from "../../redux-toolkit/services/assetsService";
import { allEventMasterDetails, setAllEventMaster } from "../../redux-toolkit/slices/eventMasterSlice";
import { allEntityMasterDetails, setAllEntityMaster } from "../../redux-toolkit/slices/entityMasterSlice";
import { allAssetsDetails, setAllAssets } from "../../redux-toolkit/slices/assetsSlice";

type Props = {
    show: boolean;
    setShow: any;
    actionType: ACTION_TYPE;
    reportName: string;
    data: any;
    setCall: any;
};

const validationSchema = Yup.object().shape({
    name: Yup.string(),
});

const EventData = (props: Props) => {

    const dispatch = useDispatch();
    const localUserObj: any = getLocalStorageData("localUser");
    const setFieldValueRef: any = useRef(null);

    //states
    const [getByIdData, setGetByIdData]: any = useState([]);
    const [file, setFile]: any = useState<File | null>(null);
    const [activeTab, setActiveTab] = useState<"manual" | "bulk">("manual");

    // ─── Event Master / Event Type ───────────────────────────────────────
    const [saveEventMaster, saveEventMasterResponse] = useSaveEventMasterMutation();
    const [saveEventType, saveEventTypeResponse] = useSaveEventTypeMutation();
    const [bulkSaveEventMaster, bulkSaveEventMasterResponse] = useBulkSaveEventMasterMutation();
    const [bulkSaveEventType, bulkSaveEventTypeResponse] = useBulkSaveEventTypeMutation();
    const [updateEventMaster, updateEventMasterResponse] = useUpdateEventMasterMutation();
    const [updateEventType, updateEventTypeResponse] = useUpdateEventTypeMutation();
    const [getEventMasterById, eventMasterByIdResponse] = useGetEventMasterByIdMutation();
    const [getEventTypeById, eventTypeByIdResponse] = useGetEventTypeByIdMutation();
    const [findAllEventType, findAllEventTypeResponse] = useFindAllEventTypeMutation();
    const allEventTypes: any = useAppSelector(allEventTypeDetails);

    // ─── Sovereign Event Ledger ──────────────────────────────────────────
    const [saveLedger, saveLedgerResponse] = useSaveLedgerMutation();
    const [updateLedger, updateLedgerResponse] = useUpdateLedgerMutation();
    const [getLedgerById, ledgerByIdResponse] = useGetLedgerByIdMutation();
    const [generateDRPlan, generateDRPlanResponse] = useGenerateDRPlanMutation();
    const [findAllEventMaster, findAllEventMasterResponse] = useFindAllEventMasterMutation();
    const [findAllEntityMaster, findAllEntityMasterResponse] = useFindAllEntityMasterMutation();
    const [findAllAssets, findAllAssetsResponse] = useFindAllAssetsMutation();

    const allEventMasters: any = useAppSelector(allEventMasterDetails);
    const allEntityMasters: any = useAppSelector(allEntityMasterDetails);
    const allAssets: any = useAppSelector(allAssetsDetails);

    // ─── useEffect: load data on open ───────────────────────────────────
    useEffect(() => {
        if (props.actionType === ACTION_TYPE.UPDATE_ACTION) {
            if (props.reportName === REPORT_NAME.EVENT_MASTER) {
                findAllEventType({ token: localUserObj?.token }); // populate event type dropdown
                getEventMasterById({ token: localUserObj?.token, id: props?.data?.id });
            } else if (props.reportName === REPORT_NAME.EVENT_TYPE) {
                getEventTypeById({ token: localUserObj?.token, id: props?.data?.id });
            } else if (props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) {
                findAllEventType({ token: localUserObj?.token });
                findAllEventMaster({ token: localUserObj?.token });
                findAllEntityMaster({ token: localUserObj?.token });
                findAllAssets({ token: localUserObj?.token });
                getLedgerById({ token: localUserObj?.token, id: props?.data?.id });
            }
        } else if (props.actionType === ACTION_TYPE.ADD_ACTION) {
            if (props.reportName === REPORT_NAME.EVENT_MASTER) {
                findAllEventType({ token: localUserObj?.token }); // populate event type dropdown
            } else if (props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) {
                findAllEventType({ token: localUserObj?.token });
                findAllEventMaster({ token: localUserObj?.token });
                findAllEntityMaster({ token: localUserObj?.token });
                findAllAssets({ token: localUserObj?.token });
            }
        }
    }, [props.reportName]);

    const handleClose = () => {
        props.setShow(false);
    };

    // ─── handleSubmit ────────────────────────────────────────────────────
    const handleSubmit = (values: any) => {
        if (props.actionType === ACTION_TYPE.ADD_ACTION) {
            if (props.reportName === REPORT_NAME.EVENT_MASTER) {
                const payload = {
                    name: values.name,
                    eventTypeId: values.eventTypeId ? parseInt(values.eventTypeId) : null,
                    description: values.description,
                    severity: values.severity,
                    status: true,
                };
                saveEventMaster({ token: localUserObj?.token, payload });
            } else if (props.reportName === REPORT_NAME.EVENT_TYPE) {
                const payload = {
                    name: values.name,
                    description: values.description,
                    riskLevel: values.riskLevel,
                    status: true,
                };
                saveEventType({ token: localUserObj?.token, payload });
            } else if (props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) {
                const payload = {
                    actionDate: values.actionDate || null,
                    eventTypeId: values.eventTypeId !== "" ? Number(values.eventTypeId) : null,
                    eventId: values.eventId !== "" ? Number(values.eventId) : null,
                    entityId: values.entityId !== "" ? Number(values.entityId) : null,
                    assetId: values.assetId !== "" ? Number(values.assetId) : null,
                    parentEntityId: values.parentEntityId !== "" ? Number(values.parentEntityId) : null,
                    userId: localUserObj?.id,
                    status: true,
                };
                saveLedger({ token: localUserObj?.token, payload });
            }

        } else if (props.actionType === ACTION_TYPE.UPDATE_ACTION) {
            if (props.reportName === REPORT_NAME.EVENT_MASTER) {
                const payload = {
                    id: props?.data?.id,
                    name: values.name,
                    eventTypeId: values.eventTypeId ? parseInt(values.eventTypeId) : null,
                    description: values.description,
                    severity: values.severity,
                    status: props?.data?.status,
                };
                updateEventMaster({ token: localUserObj?.token, payload });
            } else if (props.reportName === REPORT_NAME.EVENT_TYPE) {
                const payload = {
                    id: props?.data?.id,
                    name: values.name,
                    description: values.description,
                    riskLevel: values.riskLevel,
                    status: props?.data?.status,
                };
                updateEventType({ token: localUserObj?.token, payload });
            } else if (props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) {
                const payload = {
                    id: props?.data?.id,
                    actionDate: values.actionDate || null,
                    eventTypeId: values.eventTypeId !== "" ? Number(values.eventTypeId) : null,
                    eventId: values.eventId !== "" ? Number(values.eventId) : null,
                    entityId: values.entityId !== "" ? Number(values.entityId) : null,
                    assetId: values.assetId !== "" ? Number(values.assetId) : null,
                    parentEntityId: values.parentEntityId !== "" ? Number(values.parentEntityId) : null,
                    // LLM-populated fields
                    drStrategyType: values.drStrategyType,
                    drChosenAsset: values.drChosenAsset,
                    region: values.region,
                    costImplications: values.costImplications,
                    anticipatedLoss: values.anticipatedLoss,
                    remark: values.remark,
                    llmRequest: values.llmRequest,
                    llmResponse: values.llmResponse,
                    userId: localUserObj?.id,
                    status: props?.data?.status,
                };
                updateLedger({ token: localUserObj?.token, payload });
            }
        }
    };

    // ─── handleBulkUpload ────────────────────────────────────────────────
    const handleBulkUpload = (e: any) => {
        const fileName = e.target.files[0].name;
        const extension = fileName.lastIndexOf(".") + 1;
        const extFile = fileName.substr(extension, fileName.length).toLowerCase();
        if (extFile === "xlsx") {
            setFile(e.target.files[0]);
        } else {
            alertMessage("Only xlsx files are allowed!", "error");
        }
    };

    const handleBulkSave = () => {
        if (props.actionType === ACTION_TYPE.ADD_ACTION) {
            const formData = new FormData();
            if (file) { formData.append('file', file); }
            if (props.reportName === REPORT_NAME.EVENT_MASTER) {
                bulkSaveEventMaster({ token: localUserObj?.token, payload: formData });
            } else if (props.reportName === REPORT_NAME.EVENT_TYPE) {
                bulkSaveEventType({ token: localUserObj?.token, payload: formData });
            }
        }
    };

    // ─── Generate DR Plan (UPDATE only) ─────────────────────────────────
    const handleGenerateDRPlan = () => {
        generateDRPlan({
            token: localUserObj?.token,
            id: props?.data?.id,
        });
    };

    // ─────────────── Response Effects ────────────────────────────────────

    // Save
    useEffect(() => {
        if (saveEventMasterResponse.isSuccess) {
            alertMessage("Event Master added successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (saveEventMasterResponse.isError) {
            alertMessage(saveEventMasterResponse?.error as any, "error");
        }
    }, [saveEventMasterResponse.isSuccess, saveEventMasterResponse.isError]);

    useEffect(() => {
        if (saveEventTypeResponse.isSuccess) {
            alertMessage("Event Type added successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (saveEventTypeResponse.isError) {
            alertMessage(saveEventTypeResponse?.error as any, "error");
        }
    }, [saveEventTypeResponse.isSuccess, saveEventTypeResponse.isError]);

    useEffect(() => {
        if (saveLedgerResponse.isSuccess) {
            alertMessage("Sovereign Event Ledger added successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (saveLedgerResponse.isError) {
            alertMessage(saveLedgerResponse?.error as any, "error");
        }
    }, [saveLedgerResponse.isSuccess, saveLedgerResponse.isError]);

    // Bulk save
    useEffect(() => {
        if (bulkSaveEventMasterResponse.isSuccess) {
            alertMessage("Event Master bulk uploaded successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (bulkSaveEventMasterResponse.isError) {
            alertMessage(bulkSaveEventMasterResponse?.error as any, "error");
        }
    }, [bulkSaveEventMasterResponse.isSuccess, bulkSaveEventMasterResponse.isError]);

    useEffect(() => {
        if (bulkSaveEventTypeResponse.isSuccess) {
            alertMessage("Event Type bulk uploaded successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (bulkSaveEventTypeResponse.isError) {
            alertMessage(bulkSaveEventTypeResponse?.error as any, "error");
        }
    }, [bulkSaveEventTypeResponse.isSuccess, bulkSaveEventTypeResponse.isError]);

    // Update
    useEffect(() => {
        if (updateEventMasterResponse.isSuccess) {
            alertMessage("Event Master updated successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (updateEventMasterResponse.isError) {
            alertMessage(updateEventMasterResponse?.error as any, "error");
        }
    }, [updateEventMasterResponse.isSuccess, updateEventMasterResponse.isError]);

    useEffect(() => {
        if (updateEventTypeResponse.isSuccess) {
            alertMessage("Event Type updated successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (updateEventTypeResponse.isError) {
            alertMessage(updateEventTypeResponse?.error as any, "error");
        }
    }, [updateEventTypeResponse.isSuccess, updateEventTypeResponse.isError]);

    useEffect(() => {
        if (updateLedgerResponse.isSuccess) {
            alertMessage("Sovereign Event Ledger updated successfully", "success");
            props.setCall("Y"); handleClose();
        } else if (updateLedgerResponse.isError) {
            alertMessage(updateLedgerResponse?.error as any, "error");
        }
    }, [updateLedgerResponse.isSuccess, updateLedgerResponse.isError]);

    // getById
    useEffect(() => {
        if (eventMasterByIdResponse.isSuccess) {
            setGetByIdData(eventMasterByIdResponse.data);
        } else if (eventMasterByIdResponse.isError) {
            alertMessage(eventMasterByIdResponse?.error as any, "error");
        }
    }, [eventMasterByIdResponse.isSuccess, eventMasterByIdResponse.isError]);

    useEffect(() => {
        if (eventTypeByIdResponse.isSuccess) {
            setGetByIdData(eventTypeByIdResponse.data);
        } else if (eventTypeByIdResponse.isError) {
            alertMessage(eventTypeByIdResponse?.error as any, "error");
        }
    }, [eventTypeByIdResponse.isSuccess, eventTypeByIdResponse.isError]);

    useEffect(() => {
        if (ledgerByIdResponse.isSuccess) {
            setGetByIdData(ledgerByIdResponse.data);
        } else if (ledgerByIdResponse.isError) {
            alertMessage(ledgerByIdResponse?.error as any, "error");
        }
    }, [ledgerByIdResponse.isSuccess, ledgerByIdResponse.isError]);

    // Generate DR Plan — auto-fills fields via setFieldValue
    useEffect(() => {
        if (generateDRPlanResponse.isSuccess && generateDRPlanResponse.data) {
            const res = generateDRPlanResponse.data as any;
            const sv = setFieldValueRef.current;
            if (sv) {
                sv("drStrategyType", res.drStrategyType || res.dr_strategy_type || "");
                sv("drChosenAsset", res.drChosenAsset || res.dr_chosen_asset || "");
                sv("region", res.region || "");
                sv("costImplications", res.costImplications || res.cost_implications || "");
                sv("anticipatedLoss", res.anticipatedLoss || res.anticipated_loss || "");
                sv("remark", res.remark || "");
                sv("llmRequest", res.llmRequest || "");
                sv("llmResponse",
                    typeof res.llmResponse === "string"
                        ? res.llmResponse
                        : JSON.stringify(res.llmResponse || "", null, 2)
                );
            }
            alertMessage("DR Plan generated successfully", "success");
        } else if (generateDRPlanResponse.isError) {
            alertMessage(generateDRPlanResponse?.error as any, "error");
        }
    }, [generateDRPlanResponse.isSuccess, generateDRPlanResponse.isError]);

    // Dropdown data
    useEffect(() => {
        if (findAllEventTypeResponse.isSuccess) {
            dispatch(setAllEventType(findAllEventTypeResponse));
        } else if (findAllEventTypeResponse.isError) {
            alertMessage("Error fetching Event Types", "error");
        }
    }, [findAllEventTypeResponse.isSuccess, findAllEventTypeResponse.isError]);

    useEffect(() => {
        if (findAllEventMasterResponse.isSuccess) {
            dispatch(setAllEventMaster(findAllEventMasterResponse));
        } else if (findAllEventMasterResponse.isError) {
            alertMessage("Error fetching Events", "error");
        }
    }, [findAllEventMasterResponse.isSuccess, findAllEventMasterResponse.isError]);

    useEffect(() => {
        if (findAllEntityMasterResponse.isSuccess) {
            dispatch(setAllEntityMaster(findAllEntityMasterResponse));
        } else if (findAllEntityMasterResponse.isError) {
            alertMessage("Error fetching Entities", "error");
        }
    }, [findAllEntityMasterResponse.isSuccess, findAllEntityMasterResponse.isError]);

    useEffect(() => {
        if (findAllAssetsResponse.isSuccess) {
            dispatch(setAllAssets(findAllAssetsResponse));
        } else if (findAllAssetsResponse.isError) {
            alertMessage("Error fetching Assets", "error");
        }
    }, [findAllAssetsResponse.isSuccess, findAllAssetsResponse.isError]);

    // ─────────────── Loading Flag ─────────────────────────────────────────
    const isLoading =
        saveEventMasterResponse.isLoading ||
        saveEventTypeResponse.isLoading ||
        saveLedgerResponse.isLoading ||
        bulkSaveEventMasterResponse.isLoading ||
        bulkSaveEventTypeResponse.isLoading ||
        updateEventMasterResponse.isLoading ||
        updateEventTypeResponse.isLoading ||
        updateLedgerResponse.isLoading ||
        eventMasterByIdResponse.isLoading ||
        eventTypeByIdResponse.isLoading ||
        ledgerByIdResponse.isLoading ||
        generateDRPlanResponse.isLoading ||
        findAllEventTypeResponse.isLoading ||
        findAllEventMasterResponse.isLoading ||
        findAllEntityMasterResponse.isLoading ||
        findAllAssetsResponse.isLoading;

    // ─────────────── Formik initial values ───────────────────────────────
    const getInitialValues = () => {
        const isUpdate = props.actionType === ACTION_TYPE.UPDATE_ACTION;

        if (props.reportName === REPORT_NAME.EVENT_MASTER) {
            return {
                name: isUpdate ? (getByIdData?.name || "") : "",
                eventTypeId: isUpdate ? (getByIdData?.eventType?.id ?? getByIdData?.eventTypeId ?? "") : "",
                description: isUpdate ? (getByIdData?.description || "") : "",
                severity: isUpdate ? (getByIdData?.severity || "") : "",
            };
        }
        if (props.reportName === REPORT_NAME.EVENT_TYPE) {
            return {
                name: isUpdate ? (getByIdData?.name || "") : "",
                description: isUpdate ? (getByIdData?.description || "") : "",
                riskLevel: isUpdate ? (getByIdData?.riskLevel || "") : "",
            };
        }
        if (props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) {
            // Backend returns full nested objects (entity, asset, parentEntity, eventTypeMaster, eventMaster)
            // but the scalar ID fields (entityId, assetId, etc.) may be null — read from nested objects first.
            const rawDate = isUpdate ? (getByIdData?.actionDate || "") : "";
            // Normalise ISO timestamp → "yyyy-MM-dd" for the date input
            let actionDateVal = "";
            if (rawDate) {
                try {
                    actionDateVal = format(parseISO(rawDate), "yyyy-MM-dd");
                } catch (_) {
                    actionDateVal = rawDate;
                }
            }
            return {
                actionDate: actionDateVal,
                eventTypeId: isUpdate ? String(getByIdData?.eventType?.id ?? getByIdData?.eventTypeId ?? "") : "",
                eventId: isUpdate ? String(getByIdData?.event?.id ?? getByIdData?.eventId ?? "") : "",
                entityId: isUpdate ? String(getByIdData?.entity?.id ?? getByIdData?.entityId ?? "") : "",
                assetId: isUpdate ? String(getByIdData?.asset?.id ?? getByIdData?.assetId ?? "") : "",
                parentEntityId: isUpdate ? String(getByIdData?.parentEntity?.id ?? getByIdData?.parentEntityId ?? "") : "",
                // LLM-populated (read-only display on update)
                drStrategyType: isUpdate ? (getByIdData?.drStrategyType || "") : "",
                drChosenAsset: isUpdate ? (getByIdData?.drChosenAsset || "") : "",
                region: isUpdate ? (getByIdData?.region || "") : "",
                costImplications: isUpdate ? (getByIdData?.costImplications || "") : "",
                anticipatedLoss: isUpdate ? (getByIdData?.anticipatedLoss || "") : "",
                remark: isUpdate ? (getByIdData?.remark || "") : "",
                llmRequest: isUpdate ? (getByIdData?.llmRequest || "") : "",
                llmResponse: isUpdate ? (getByIdData?.llmResponse || "") : "",
            };
        }
        return {};
    };

    // ─────────────── Render ───────────────────────────────────────────────
    return (
        <>
            <Modal
                show={props.show}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold text-accent" style={{ fontSize: "1rem" }}>
                        {props.actionType === ACTION_TYPE.ADD_ACTION ? "Add" : "Update"}{" "}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading ? <Loading /> : ""}

                    {/* Bulk / Manual tabs — only for Event Master and Event Type */}
                    {props.actionType === ACTION_TYPE.ADD_ACTION &&
                        props.reportName !== REPORT_NAME.SOVEREIGN_EVENT_LEDGER && (
                            <ul className="nav nav-tabs mb-3" role="tablist">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'manual' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('manual')}
                                    >
                                        Manual Entry
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'bulk' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('bulk')}
                                    >
                                        Bulk Upload
                                    </button>
                                </li>
                            </ul>
                        )}

                    {/* ── BULK TAB ── */}
                    {activeTab === "bulk" &&
                        props.actionType === ACTION_TYPE.ADD_ACTION &&
                        props.reportName !== REPORT_NAME.SOVEREIGN_EVENT_LEDGER && (
                            <div>
                                <div className="row">
                                    <div className="col-md-6 mt-2">
                                        <label className="form-label">Upload File (xlsx)</label>
                                        <input
                                            type="file"
                                            accept=".xlsx"
                                            className="form-control form-textbox"
                                            onChange={handleBulkUpload}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 mt-3 d-flex justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-primary bg-accent border-0"
                                        onClick={handleBulkSave}
                                    >
                                        Upload
                                    </button>
                                </div>
                            </div>
                        )}

                    {/* ── MANUAL TAB (or always for SOVEREIGN_EVENT_LEDGER) ── */}
                    {(activeTab === "manual" || props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER) && (
                        <Formik
                            validationSchema={validationSchema}
                            validateOnChange={true}
                            validateOnBlur={true}
                            enableReinitialize={true}
                            initialValues={getInitialValues()}
                            onSubmit={(values: any, { resetForm }) => {
                                handleSubmit(values);
                                resetForm();
                            }}
                        >
                            {({ values, errors, touched, handleChange, handleSubmit: formikSubmit, setFieldValue }) => {
                                setFieldValueRef.current = setFieldValue;
                                return (
                                    <Form onSubmit={formikSubmit}>
                                        <div className="row">

                                            {/* ── EVENT MASTER ── */}
                                            {props.reportName === REPORT_NAME.EVENT_MASTER && (
                                                <>
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">
                                                            Name <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={values.name}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            placeholder="Enter name"
                                                        />
                                                        {errors.name && touched.name && (
                                                            <div className="text-danger mt-1">{String(errors.name)}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Event Type</label>
                                                        <select
                                                            name="eventTypeId"
                                                            value={values.eventTypeId}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Event Type</option>
                                                            {allEventTypes?.map((et: any) => (
                                                                <option key={et.id} value={et.id}>{et.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Severity</label>
                                                        <input
                                                            type="text"
                                                            name="severity"
                                                            value={values.severity}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            placeholder="e.g. Low, Medium, High"
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mt-2">
                                                        <label className="form-label">Description</label>
                                                        <textarea
                                                            name="description"
                                                            value={values.description}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            rows={3}
                                                            placeholder="Enter description"
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {/* ── EVENT TYPE ── */}
                                            {props.reportName === REPORT_NAME.EVENT_TYPE && (
                                                <>
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">
                                                            Name <span className="text-danger">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            name="name"
                                                            value={values.name}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            placeholder="Enter name"
                                                        />
                                                        {errors.name && touched.name && (
                                                            <div className="text-danger mt-1">{String(errors.name)}</div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Risk Level</label>
                                                        <input
                                                            type="text"
                                                            name="riskLevel"
                                                            value={values.riskLevel}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            placeholder="e.g. Low, Medium, High"
                                                        />
                                                    </div>
                                                    <div className="col-md-12 mt-2">
                                                        <label className="form-label">Description</label>
                                                        <textarea
                                                            name="description"
                                                            value={values.description}
                                                            onChange={handleChange}
                                                            className="form-control form-textbox"
                                                            rows={3}
                                                            placeholder="Enter description"
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {/* ── SOVEREIGN EVENT LEDGER ── */}
                                            {props.reportName === REPORT_NAME.SOVEREIGN_EVENT_LEDGER && (
                                                <>
                                                    {/* Action Date */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Action Date</label>
                                                        <input
                                                            type="date"
                                                            name="actionDate"
                                                            className="form-control form-textbox"
                                                            onChange={handleChange}
                                                            value={values.actionDate || ""}
                                                        />
                                                    </div>
                                                    {/* Event Type */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Event Type</label>
                                                        <select
                                                            name="eventTypeId"
                                                            value={values.eventTypeId}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Event Type</option>
                                                            {allEventTypes?.map((et: any) => (
                                                                <option key={et.id} value={et.id}>{et.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Event */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Event</label>
                                                        <select
                                                            name="eventId"
                                                            value={String(values.eventId ?? "")}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Event</option>
                                                            {allEventMasters?.map((em: any) => (
                                                                <option key={em.id} value={String(em.id)}>{em.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Entity */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Entity</label>
                                                        <select
                                                            name="entityId"
                                                            value={String(values.entityId ?? "")}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Entity</option>
                                                            {allEntityMasters?.map((em: any) => (
                                                                <option key={em.id} value={String(em.id)}>{em.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Asset */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Asset</label>
                                                        <select
                                                            name="assetId"
                                                            value={String(values.assetId ?? "")}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Asset</option>
                                                            {allAssets?.map((a: any) => (
                                                                <option key={a.id} value={String(a.id)}>{a.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Parent Entity */}
                                                    <div className="col-md-6 mt-2">
                                                        <label className="form-label">Parent Entity</label>
                                                        <select
                                                            name="parentEntityId"
                                                            value={values.parentEntityId}
                                                            onChange={handleChange}
                                                            className="form-select form-textbox"
                                                        >
                                                            <option value="">Select Parent Entity</option>
                                                            {allEntityMasters?.map((em: any) => (
                                                                <option key={em.id} value={em.id}>{em.name}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* ── LLM DR Plan section — only on UPDATE ── */}
                                                    {props.actionType === ACTION_TYPE.UPDATE_ACTION && (
                                                        <>
                                                            <div className="col-12 mt-3 mb-1">
                                                                <hr />
                                                                <div className="d-flex align-items-center justify-content-between">
                                                                    <label className="fw-semibold">DR Plan (AI Generated)</label>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        disabled={generateDRPlanResponse.isLoading}
                                                                        onClick={() => handleGenerateDRPlan()}
                                                                    >
                                                                        {generateDRPlanResponse.isLoading ? "Generating..." : "Generate DR Plan"}
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">DR Strategy Type</label>
                                                                <input
                                                                    type="text"
                                                                    name="drStrategyType"
                                                                    value={values.drStrategyType}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">DR Chosen Asset</label>
                                                                <input
                                                                    type="text"
                                                                    name="drChosenAsset"
                                                                    value={values.drChosenAsset}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">Region</label>
                                                                <input
                                                                    type="text"
                                                                    name="region"
                                                                    value={values.region}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">Cost Implications</label>
                                                                <input
                                                                    type="text"
                                                                    name="costImplications"
                                                                    value={values.costImplications}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">Anticipated Loss</label>
                                                                <input
                                                                    type="text"
                                                                    name="anticipatedLoss"
                                                                    value={values.anticipatedLoss}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-12 mt-2">
                                                                <label className="form-label">Remark</label>
                                                                <textarea
                                                                    name="remark"
                                                                    value={values.remark}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    rows={2}
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">LLM Request</label>
                                                                <textarea
                                                                    name="llmRequest"
                                                                    value={values.llmRequest}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    rows={2}
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>

                                                            <div className="col-md-6 mt-2">
                                                                <label className="form-label">LLM Response</label>
                                                                <textarea
                                                                    name="llmResponse"
                                                                    value={values.llmResponse}
                                                                    onChange={handleChange}
                                                                    className="form-control form-textbox"
                                                                    rows={2}
                                                                    placeholder="AI generated"
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            )}

                                        </div>{/* end .row */}

                                        {/* ── Footer Buttons ── */}
                                        <div className="col-12 mt-3 d-flex justify-content-end">
                                            <button
                                                type="submit"
                                                className="btn btn-primary bg-accent border-0"
                                            >
                                                {props.actionType === ACTION_TYPE.ADD_ACTION ? "Save" : "Update"}
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default EventData;
